// The EUSDR questions InRange needs Amazon to settle, each with its replies
// (docs/Y2026-Q4/design-eusdr-questions.md). Mirrors src/inrange_agents/eusdr_questions.py.
import { useMutation, useQuery, useQueryClient } from "react-query";

import type { PostJsonError } from "../http/postJson";
import { QK } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";

export type EusdrStatus = "awaiting" | "discussion" | "answered";
/** What a reply does to the question's status; a reply that does this needs no text. */
export type EusdrMark = "answered" | "reopened";
/** Which of a conflict's clauses applies: A, B, or neither as written. */
export type EusdrRuling = "a" | "b" | "neither";

/** One reference on a source line, and the document it names (a key of `documents`), if
 * any. */
export interface EusdrSourceRef {
  text: string;
  document: string | null;
}

/** One line of the documents' words: its citation, what it says and the document it is
 * from; a lead-in line has no citation. */
export interface EusdrWords {
  citation: string | null;
  text: string;
  document: string | null;
  /** Lines with the same number belong together, such as a reference and what the EUSDR
   * holds there now. */
  group: number;
}

/** A document the references name, and the Global data file that holds it. */
export interface EusdrDocument {
  global_data_id: string;
  title: string;
  file_name: string;
  content_type: string;
  has_text: boolean;
}

/** One side of a conflict. */
export interface EusdrClause {
  /** What it requires, in a few words. */
  says: string;
  source: string;
  source_refs: EusdrSourceRef[];
  /** The document's own words. */
  text: string;
}

export interface EusdrReply {
  reply_id: string;
  /** How the overview names the writer to this viewer. */
  name: string;
  side: "inrange" | "amazon";
  text: string;
  answer: string | null;
  marks: EusdrMark | null;
  ruling: EusdrRuling | null;
  at: string;
  /** Written by the signed-in viewer, who may remove it. */
  mine: boolean;
}

export interface EusdrQuestion {
  key: string;
  /** The top-level question's number; a part shares its parent's. */
  number: number;
  /** A part's place under its parent (1, 2, ...), 0 for a top-level question. */
  sub: number;
  /** "Q2" or "Q2.1". */
  label: string;
  /** The question this is a part of. */
  parent: string | null;
  /** Asked only through its parts: its status follows theirs, and it takes no answer. */
  group: boolean;
  /** Asked after everything else: sorted below the other open questions. */
  low_priority: boolean;
  /** The download's tab it goes in: the conflicts and the section references have their own. */
  sheet: "questions" | "conflicts" | "references";
  category: string;
  /** Process overview step ids, or "general". */
  steps: string[];
  topic: string;
  /** The question; empty for a group, and for a part its topic says it all. */
  question: string;
  context: string;
  verbatim: string;
  /** `verbatim` line by line. */
  words: EusdrWords[];
  source: string;
  source_refs: EusdrSourceRef[];
  status: EusdrStatus;
  /** The latest answer a reply recorded since the question was last reopened. */
  answer: string | null;
  /** A conflict's two clauses, A then B; empty for any other question. */
  clauses: EusdrClause[];
  /** On a conflict, the clause ruled to apply since it was last reopened. */
  ruling: EusdrRuling | null;
  replies: EusdrReply[];
}

export interface EusdrQuestions {
  questions: EusdrQuestion[];
  categories: string[];
  /** Every step a question can sit on, in Process overview's order. */
  steps: { id: string; title: string }[];
  answers: { key: string; label: string }[];
  /** The documents InRange staff can open, by key; empty for an Amazon viewer. */
  documents: Record<string, EusdrDocument>;
}

export interface EusdrReplyInput {
  key: string;
  text: string;
  answer: string | null;
  marks: EusdrMark | null;
  ruling?: EusdrRuling | null;
}

export const useEusdrQuestions = () => {
  const http = useAgentsHttp();
  return useQuery<EusdrQuestions>(
    [QK.amazonEusdrQuestions],
    () => http<EusdrQuestions>("/amazon-overview/eusdr-questions/list", {}),
    { refetchInterval: 30_000, refetchOnWindowFocus: true },
  );
};

const message = (err: PostJsonError): Error => {
  try {
    const body = JSON.parse(err.bodyText ?? "") as { message?: string };
    if (body.message) return new Error(body.message);
  } catch {
    // Not JSON: the status line says what happened.
  }
  return err;
};

export const useReplyToEusdrQuestion = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ reply_id: string }, Error, EusdrReplyInput>(
    (body) =>
      http<{ reply_id: string }>("/amazon-overview/eusdr-questions/reply", body).catch(
        (err: PostJsonError) => {
          throw message(err);
        },
      ),
    {
      // The reply form shows its own error beside it.
      meta: { silentError: true },
      onSettled: () => qc.invalidateQueries([QK.amazonEusdrQuestions]),
    },
  );
};

export const useRemoveEusdrReply = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ removed: boolean }, Error, { reply_id: string }>(
    (body) =>
      http<{ removed: boolean }>("/amazon-overview/eusdr-questions/remove", body).catch(
        (err: PostJsonError) => {
          throw message(err);
        },
      ),
    {
      // The thread shows its own error beside the response.
      meta: { silentError: true },
      onSettled: () => qc.invalidateQueries([QK.amazonEusdrQuestions]),
    },
  );
};
